import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Plus, Check, X, ShieldAlert } from 'lucide-react';

const fmtUsd = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border border-amber-200',
  executed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border border-rose-200',
};

export default function FinanceRefundsTab() {
  const [orgs, setOrgs] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showRaise, setShowRaise] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ account_id: '', amount_usd: '', reason: '' });

  useEffect(() => {
    api.get('/finance/orgs').then((r) => setOrgs(r.data?.orgs || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/finance/refunds', { params });
      setRefunds(data?.refunds || []);
      setCanApprove(!!data?.can_approve);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const raise = async () => {
    if (!form.account_id) return toast.error('Select an organisation');
    const amt = Number(form.amount_usd);
    if (!(amt > 0)) return toast.error('Enter a positive amount');
    setSaving(true);
    try {
      await api.post('/finance/refunds', {
        account_id: form.account_id, amount_usd: amt, reason: form.reason,
      });
      toast.success('Refund raised — pending Owner approval');
      setShowRaise(false);
      setForm({ account_id: '', amount_usd: '', reason: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise refund');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/finance/refunds/${id}/approve`);
      toast.success('Refund approved & executed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejecting this refund? (optional)') ?? '';
    setBusyId(id);
    try {
      await api.post(`/finance/refunds/${id}/reject`, { reason });
      toast.success('Refund rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-refunds-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Refunds
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Raise a SaaS refund for review. Approval is restricted to platform Owners (Hari, Domenic, Satish).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-none w-40" data-testid="finance-refunds-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="executed">Executed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="rounded-none" onClick={load} data-testid="finance-refunds-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button className="rounded-none" onClick={() => setShowRaise(true)} data-testid="finance-raise-refund-btn">
            <Plus className="h-4 w-4 mr-2" /> Raise refund
          </Button>
        </div>
      </div>

      {!canApprove && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground border border-dashed border-border p-3 bg-muted/30">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>You can raise and view refunds. Approval is limited to Owners (Hari, Domenic, Satish).</span>
        </div>
      )}

      <div className="border border-border bg-card">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : refunds.length === 0 ? (
          <div className="p-8 text-sm text-muted-foreground text-center">No refunds yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-refunds-table">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-medium">Raised</th>
                  <th className="px-4 py-2 font-medium">Organisation</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 font-medium">Raised by</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((r) => (
                  <tr key={r.id} className="border-b border-border/60" data-testid={`finance-refund-row-${r.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.raised_at)}</td>
                    <td className="px-4 py-2">{r.org_name || '—'}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">{fmtUsd(r.amount_usd)}</td>
                    <td className="px-4 py-2 max-w-[240px] truncate" title={r.reason}>{r.reason || '—'}</td>
                    <td className="px-4 py-2">{r.raised_by_name || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full capitalize ${STATUS_STYLES[r.status] || ''}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {r.status === 'pending' && canApprove ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="rounded-none h-7 text-emerald-600 hover:text-emerald-700"
                                  disabled={busyId === r.id} onClick={() => approve(r.id)}
                                  data-testid={`finance-approve-${r.id}`}>
                            {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            <span className="ml-1">Approve</span>
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-none h-7 text-rose-600 hover:text-rose-700"
                                  disabled={busyId === r.id} onClick={() => reject(r.id)}
                                  data-testid={`finance-reject-${r.id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : r.status === 'executed' ? (
                        <span className="text-xs text-muted-foreground">by {r.approved_by_name || '—'}</span>
                      ) : r.status === 'pending' ? (
                        <span className="text-xs text-muted-foreground italic">awaiting Owner</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Raise refund dialog */}
      <Dialog open={showRaise} onOpenChange={setShowRaise}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Raise a refund</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Organisation</Label>
              <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="finance-raise-org">
                  <SelectValue placeholder="Select organisation" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {orgs.map((o) => (
                    <SelectItem key={o.account_id} value={o.account_id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount_usd}
                     onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                     className="rounded-none" placeholder="0.00" data-testid="finance-raise-amount" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        className="rounded-none" rows={3} placeholder="e.g. Duplicate subscription charge"
                        data-testid="finance-raise-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowRaise(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={raise} disabled={saving} data-testid="finance-raise-submit">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Raise refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
